/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client';

import { type ComponentProps, type JSX, type MouseEvent, useCallback, useMemo } from 'react';
import { ArrowLeft, ArrowRight, MoreHorizontal } from 'lucide-react';
import { useLocation, useNavigation } from 'react-router';

import { cn } from '@/lib/utils';
import { useNavigate } from '@/hooks/use-navigate';
import { Button, type ButtonProps } from '@/components/buttons';
import { getPaginationItems } from '@/lib/pagination-utils';

function Pagination({ className, ...props }: ComponentProps<'nav'>) {
    return (
        <nav
            role="navigation"
            aria-label="pagination"
            data-slot="pagination"
            className={cn('flex w-full items-center justify-between border-t border-border py-6', className)}
            {...props}
        />
    );
}

function PaginationContent({ className, ...props }: ComponentProps<'ul'>) {
    return (
        <ul data-slot="pagination-content" className={cn('flex flex-row items-center gap-1', className)} {...props} />
    );
}

function PaginationItem({ className, ...props }: ComponentProps<'li'>) {
    return <li data-slot="pagination-item" className={cn('list-none', className)} {...props} />;
}

type PaginationLinkProps = {
    isActive?: boolean;
    disabled?: boolean;
} & ComponentProps<'a'>;

function PaginationLink({ className, isActive, disabled, children, ...props }: PaginationLinkProps) {
    return (
        <a
            aria-current={isActive ? 'page' : undefined}
            aria-disabled={disabled || undefined}
            data-slot="pagination-link"
            data-active={isActive || undefined}
            tabIndex={disabled ? -1 : 0}
            className={cn(
                'inline-flex size-9 items-center justify-center rounded-xs text-sm leading-5 text-foreground transition-colors outline-none select-none',
                'hover:bg-secondary',
                'focus-visible:bg-background focus-visible:border focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-focus',
                isActive && 'border border-input bg-background shadow-xs hover:bg-secondary focus-visible:border-ring',
                disabled && 'pointer-events-none opacity-50',
                className
            )}
            {...props}>
            {children}
        </a>
    );
}

type PaginationNavButtonProps = ComponentProps<'a'> & {
    disabled?: boolean;
    variant?: ButtonProps['variant'];
};

function PaginationPrevious({ className, disabled, variant = 'ghost', children, ...props }: PaginationNavButtonProps) {
    return (
        <Button
            asChild
            variant={variant}
            className={cn('h-9 gap-1 px-2.5 py-2', disabled && 'pointer-events-none opacity-50', className)}>
            <a
                aria-label="Go to previous page"
                aria-disabled={disabled || undefined}
                data-slot="pagination-previous"
                tabIndex={disabled ? -1 : 0}
                {...props}>
                <ArrowLeft aria-hidden="true" className="size-4" />
                <span>{children ?? 'Previous'}</span>
            </a>
        </Button>
    );
}

function PaginationNext({ className, disabled, variant = 'ghost', children, ...props }: PaginationNavButtonProps) {
    return (
        <Button
            asChild
            variant={variant}
            className={cn('h-9 gap-1 px-2.5 py-2', disabled && 'pointer-events-none opacity-50', className)}>
            <a
                aria-label="Go to next page"
                aria-disabled={disabled || undefined}
                data-slot="pagination-next"
                tabIndex={disabled ? -1 : 0}
                {...props}>
                <span>{children ?? 'Next'}</span>
                <ArrowRight aria-hidden="true" className="size-4" />
            </a>
        </Button>
    );
}

function PaginationEllipsis({ className, ...props }: ComponentProps<'span'>) {
    return (
        <span
            aria-hidden="true"
            data-slot="pagination-ellipsis"
            className={cn('inline-flex size-9 items-center justify-center text-foreground', className)}
            {...props}>
            <MoreHorizontal className="size-4" />
            <span className="sr-only">More pages</span>
        </span>
    );
}

type PaginationControlProps = {
    /** Page size (items per page). */
    limit: number;
    /** Current 0-based offset. */
    offset: number;
    /** Total number of items across all pages. */
    total: number;
    /** Optional className applied to the root <nav>. */
    className?: string;
};

/**
 * URL-driven Pagination control for offset-based list APIs (e.g. SCAPI product search).
 *
 * Renders the Figma pagination layout (top border, "Previous" / numbered pages / "Next") and
 * navigates by rewriting the `offset` search param while preserving every other param. While a
 * navigation is in-flight the destination page is highlighted optimistically via
 * `useNavigation`, and the whole control is dimmed and made non-interactive. Returns `null`
 * when there's a single page or less.
 *
 * Use this as the drop-in pagination for PLP / search routes:
 *   `<Pagination limit={limit} offset={result.offset} total={result.total} />`
 *
 * For custom layouts, compose the named primitives instead (`Pagination`, `PaginationContent`,
 * `PaginationLink`, etc.).
 */
function PaginationControl({ limit, offset, total, className }: PaginationControlProps): JSX.Element | null {
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const isPending = navigation.state !== 'idle';

    /**
     * Optimistic offset derived from the in-flight navigation target.
     *
     * While a navigation is pending, `navigation.location` holds the target location, so we can
     * highlight the destination page before the loader settles. Once it settles we fall back to
     * the server-provided offset.
     */
    const effectiveOffset = navigation.location
        ? Number(new URLSearchParams(navigation.location.search).get('offset') || offset)
        : offset;

    const safeLimit = Math.max(1, limit);
    const totalPages = Math.max(1, Math.ceil(total / safeLimit));
    const current = Math.min(totalPages, Math.max(1, Math.floor(effectiveOffset / safeLimit) + 1));
    const pageItems = useMemo(() => getPaginationItems(totalPages, current), [totalPages, current]);

    const buildSearch = useCallback(
        (page: number) => {
            const params = new URLSearchParams(location.search);
            params.set('offset', String((page - 1) * safeLimit));
            return `?${params.toString()}`;
        },
        [location.search, safeLimit]
    );

    const navigatePage = useCallback(
        (page: number) => {
            const target = Math.min(totalPages, Math.max(1, page));
            if (target === current) return;
            void navigate({ ...location, search: buildSearch(target) });
        },
        [buildSearch, current, location, navigate, totalPages]
    );

    if (totalPages <= 1) {
        return null;
    }

    const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>, page: number) => {
        // Allow the browser to handle modified clicks (ctrl/cmd/shift/alt) so users can open the
        // page in a new tab from the rendered href.
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigatePage(page);
    };

    return (
        <Pagination
            aria-label="Pagination"
            className={cn(isPending && 'pointer-events-none opacity-50 transition-opacity', className)}>
            <PaginationPrevious
                href={buildSearch(Math.max(1, current - 1))}
                disabled={current <= 1}
                onClick={(event) => handleLinkClick(event, current - 1)}
            />
            <PaginationContent>
                {pageItems.map((item) =>
                    typeof item === 'object' ? (
                        <PaginationItem key={`ellipsis-${item.key}`}>
                            <PaginationEllipsis />
                        </PaginationItem>
                    ) : (
                        <PaginationItem key={item}>
                            <PaginationLink
                                href={buildSearch(item)}
                                isActive={current === item}
                                aria-label={`Page ${String(item)}`}
                                onClick={(event) => handleLinkClick(event, item)}>
                                {item}
                            </PaginationLink>
                        </PaginationItem>
                    )
                )}
            </PaginationContent>
            <PaginationNext
                href={buildSearch(Math.min(totalPages, current + 1))}
                disabled={current === totalPages}
                onClick={(event) => handleLinkClick(event, current + 1)}
            />
        </Pagination>
    );
}

PaginationControl.displayName = 'Pagination';

export default PaginationControl;

export {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationPrevious,
    PaginationNext,
    PaginationEllipsis,
};
